<template>
  <div class="config-panel">
    <div class="panel-header">
      <div>
        <h3>Configure Destination</h3>
        <p>Specify where to load your data</p>
      </div>
      <button @click="$emit('close')" class="btn-close">
        <X :size="20" />
      </button>
    </div>

    <div class="panel-body">
      <!-- Table Creation Mode Toggle -->
      <div class="form-group">
        <label>Table Selection</label>
        <div class="toggle-group">
          <button
            @click="localData.createNewTable = true"
            :class="['toggle-btn', { active: localData.createNewTable }]"
          >
            Create New Table
          </button>
          <button
            @click="localData.createNewTable = false"
            :class="['toggle-btn', { active: !localData.createNewTable }]"
          >
            Use Existing Table
          </button>
        </div>
      </div>

      <!-- New Table Name Input -->
      <div v-if="localData.createNewTable" class="form-group">
        <label>Table Name *</label>
        <input
          v-model="localData.tableName"
          placeholder="e.g., sales_data"
          class="form-input"
        />
      </div>

      <!-- Existing Table Selector -->
      <div v-else class="form-group">
        <label>Select Existing Table *</label>
        <select v-model="localData.tableName" class="form-select" @change="onTableSelected">
          <option value="">-- Select a table --</option>
          <option v-for="table in existingTables" :key="table" :value="table">
            {{ table }}
          </option>
        </select>
        <p v-if="loadingTables" class="field-hint">Loading tables...</p>
        <p v-if="validationMessage" :class="['validation-message', validationMessage.type]">
          {{ validationMessage.text }}
        </p>
      </div>

      <div class="form-group">
        <label>Sync Mode</label>
        <select v-model="localData.syncMode" class="form-select">
          <option value="AppendAll">Append all records</option>
          <option value="ReplaceAll">Replace all data</option>
          <option value="SyncChanges">Sync changes (upsert)</option>
        </select>
        <p class="field-hint">{{ getSyncModeDescription(localData.syncMode) }}</p>
      </div>

      <!-- Unique Key Columns Selection (only for SyncChanges mode) -->
      <div v-if="localData.syncMode === 'SyncChanges'" class="form-group">
        <label>Unique Key Columns *</label>
        <div class="unique-keys-list">
          <label
            v-for="column in availableColumns"
            :key="column"
            class="checkbox-label"
          >
            <input
              type="checkbox"
              :value="column"
              v-model="localData.uniqueKeyColumns"
            />
            <span>{{ column }}</span>
          </label>
        </div>
        <p class="field-hint">Select columns that uniquely identify each record</p>
      </div>

      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="localData.createIfNotExists" />
          <span>Create table if it doesn't exist</span>
        </label>
      </div>

      <!-- Summary Card -->
      <div v-if="localData.tableName" class="info-card">
        <div class="info-header">
          <Target :size="16" />
          <span>Configuration Summary</span>
        </div>
        <div class="info-content">
          <div class="info-item">
            <span class="label">Table:</span>
            <span class="value">{{ localData.tableName }}</span>
          </div>
          <div class="info-item">
            <span class="label">Sync Mode:</span>
            <span class="value">{{ formatSyncMode(localData.syncMode) }}</span>
          </div>
          <div v-if="localData.syncMode === 'SyncChanges' && localData.uniqueKeyColumns.length > 0" class="info-item">
            <span class="label">Unique Keys:</span>
            <span class="value">{{ localData.uniqueKeyColumns.join(', ') }}</span>
          </div>
          <div class="info-item">
            <span class="label">Table Mode:</span>
            <span class="value">{{ localData.createNewTable ? 'Create New' : 'Use Existing' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="panel-footer">
      <button @click="$emit('close')" class="btn btn-secondary">
        Cancel
      </button>
      <button 
        @click="saveConfig" 
        class="btn btn-primary"
        :disabled="!isValid"
      >
        Apply Changes
      </button>
    </div>
  </div>
</template>

<script setup>
// @ts-nocheck - Will be properly typed later
import { ref, computed, onMounted } from 'vue'
import { X, Target } from 'lucide-vue-next'
import api from '@/Modules/ETL/services/api'

const props = defineProps({
  node: {
    type: Object,
    required: true
  },
  transformNode: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update', 'close'])

// State
const existingTables = ref([])
const loadingTables = ref(false)
const validationMessage = ref(null)

// Get available columns from transform node
const availableColumns = computed(() => {
  if (!props.transformNode?.data?.columnMappings) return []
  return props.transformNode.data.columnMappings
    .filter(m => m.Include)
    .map(m => m.TargetColumn)
})

const localData = ref({
  tableName: props.node.data.tableName || '',
  syncMode: props.node.data.syncMode || 'AppendAll', // Backend enum: AppendAll, ReplaceAll, SyncChanges
  uniqueKeyColumns: props.node.data.uniqueKeyColumns || [],
  createNewTable: props.node.data.createNewTable !== undefined
    ? props.node.data.createNewTable
    : true
})

// Load existing tables on mount
onMounted(async () => {
  await loadExistingTables()
})

const loadExistingTables = async () => {
  try {
    loadingTables.value = true
    const response = await api.getUserTables()
    existingTables.value = response.data || []
  } catch (error) {
    console.error('Failed to load tables:', error)
    existingTables.value = []
  } finally {
    loadingTables.value = false
  }
}

const onTableSelected = async () => {
  if (!localData.value.tableName || !props.transformNode?.data?.columnMappings) {
    validationMessage.value = null
    return
  }

  try {
    validationMessage.value = { text: 'Validating schema...', type: 'info' }

    const columnMappings = props.transformNode.data.columnMappings
      .filter(m => m.Include)
      .map(m => ({
        sourceColumn: m.SourceColumn,
        targetColumn: m.TargetColumn,
        include: m.Include,
        dataType: m.DataType
      }))

    const response = await api.validateTableCompatibility(
      localData.value.tableName,
      columnMappings
    )

    if (response.data.compatible) {
      validationMessage.value = {
        text: '✓ Table is compatible with your data',
        type: 'success'
      }
    } else {
      validationMessage.value = {
        text: `⚠ ${response.data.message}`,
        type: 'warning'
      }
    }
  } catch (error) {
    validationMessage.value = {
      text: `✗ Validation failed: ${error.errorMessage}`,
      type: 'error'
    }
  }
}

const isValid = computed(() => {
  if (!localData.value.tableName || localData.value.tableName.trim().length === 0) {
    return false
  }
  // If SyncChanges mode, require at least one unique key column
  if (localData.value.syncMode === 'SyncChanges' && localData.value.uniqueKeyColumns.length === 0) {
    return false
  }
  return true
})

const getSyncModeDescription = (mode) => {
  const descriptions = {
    AppendAll: 'Add new records to the table, keeping all history',
    ReplaceAll: 'Delete all existing data and load fresh records',
    SyncChanges: 'Add new records and update changed records based on unique key'
  }
  return descriptions[mode] || ''
}

const formatSyncMode = (mode) => {
  const modes = {
    AppendAll: 'Append all records',
    ReplaceAll: 'Replace all data',
    SyncChanges: 'Sync changes (upsert)'
  }
  return modes[mode] || mode
}

const saveConfig = () => {
  emit('update', {
    tableName: localData.value.tableName,
    syncMode: localData.value.syncMode,
    uniqueKeyColumns: localData.value.uniqueKeyColumns,
    createNewTable: localData.value.createNewTable,
    label: `${localData.value.tableName}`
  })
  emit('close')
}
</script>

<style scoped>
.config-panel {
  position: fixed;
  top: 78px;
  right: 0;
  width: 450px;
  height: calc(100vh - 78px);
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-header h3 {
  margin: 0 0 0.25rem 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.panel-header p {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.btn-close {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-text-primary);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 14px;
  color: var(--color-text-primary);
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.info-card {
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1.5rem;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-success);
  margin-bottom: 0.75rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.info-item .label {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.info-item .value {
  color: var(--color-text-primary);
  font-family: 'Monaco', 'Consolas', monospace;
  font-weight: 600;
}

.panel-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.02);
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.05);
}

.btn-primary {
  background: linear-gradient(135deg, var(--color-success), #059669);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field-hint {
  margin-top: 0.5rem;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-style: italic;
}

.unique-keys-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.unique-keys-list .checkbox-label {
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.unique-keys-list .checkbox-label:hover {
  border-color: var(--color-accent);
  background: rgba(102, 126, 234, 0.05);
}

.toggle-group {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
}

.toggle-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: var(--color-text-secondary);
}

.toggle-btn.active {
  background: white;
  color: var(--color-accent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.5);
}

.validation-message {
  margin-top: 0.5rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
}

.validation-message.success {
  background: rgba(34, 197, 94, 0.1);
  color: var(--color-success);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.validation-message.warning {
  background: rgba(251, 191, 36, 0.1);
  color: #d97706;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.validation-message.error {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.validation-message.info {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}
</style>